const PAGINATION_FALLBACK = { total: 0, page: 1, limit: 20, pages: 1 };

export function unwrapList(res, limit = 20) {
  const body = res?.data ?? res;
  if (Array.isArray(body)) {
    return {
      items: body,
      pagination: { ...PAGINATION_FALLBACK, total: body.length, limit, pages: 1 },
    };
  }
  const items =
    body?.items ?? body?.docs ?? body?.results ?? (Array.isArray(body?.data) ? body.data : []) ?? [];
  const pagination = body?.pagination ?? {
    ...PAGINATION_FALLBACK,
    total: items.length,
    limit,
    pages: 1,
  };
  return { items: Array.isArray(items) ? items : [], pagination };
}
export function pick(record, ...keys) {
  if (!record) return undefined;
  for (const key of keys) {
    const value = key.split(".").reduce((acc, part) => (acc == null ? acc : acc[part]), record);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
}

export function refLabel(value, fallback = "—") {
  if (value == null || value === "") return fallback;
  if (typeof value === "string") {
    return /^[0-9a-f]{24}$/i.test(value) ? fallback : value;
  }
  if (typeof value === "object") return value.name ?? value.title ?? value.label ?? fallback;
  return String(value);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatDate(value) {
  if (!value) return "";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getDate()).padStart(2, "0")} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${formatDate(value)} · ${formatTime(value)}`;
}

export function formatTime(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{1,2}:\d{2}$/.test(value)) return formatClockLong(value);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const h24 = date.getHours();
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(date.getMinutes()).padStart(2, "0")} ${h24 >= 12 ? "PM" : "AM"}`;
}

export function humanise(value) {
  if (!value || typeof value !== "string") return "";
  const spaced = value.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function fieldErrors(err) {
  const apiErrors = err?.response?.data?.errors;
  if (!apiErrors?.length) return null;
  const map = {};
  apiErrors.forEach((e) => {
    if (e?.path) map[e.path] = e.message;
  });
  return Object.keys(map).length ? map : null;
}

export function errorMessage(err, fallback = "Something went wrong. Please try again.") {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.errors?.[0]?.message ||
    err?.message ||
    fallback
  );
}


export function todayISO() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

export function currentMonth() {
  return todayISO().slice(0, 7);
}

export function monthRange(month = currentMonth()) {
  const [y, m] = month.split("-").map(Number);
  const first = `${month}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const today = todayISO();
  const last = `${month}-${String(lastDay).padStart(2, "0")}`;
  return { from: first, to: last > today ? today : last };
}

export function shiftMonth(month, n) {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function formatMonth(month) {
  if (!month) return "";
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

function parseClock(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || "");
  if (!m) return null;
  const h24 = Number(m[1]);
  const min = Number(m[2]);
  if (h24 > 23 || min > 59) return null;
  return { h24, min, h12: h24 % 12 === 0 ? 12 : h24 % 12, meridiem: h24 >= 12 ? "PM" : "AM" };
}

export function formatClock(hhmm) {
  const t = parseClock(hhmm);
  if (!t) return "";
  const time = t.min === 0 ? String(t.h12) : `${t.h12}:${String(t.min).padStart(2, "0")}`;
  return `${time} ${t.meridiem}`;
}

export function formatClockLong(hhmm) {
  const t = parseClock(hhmm);
  if (!t) return "";
  return `${t.h12}:${String(t.min).padStart(2, "0")} ${t.meridiem}`;
}
export function formatShiftRange(start, end) {
  const a = formatClock(start);
  const b = formatClock(end);
  return a && b ? `${a} to ${b}` : "";
}

export function formatMinutes(minutes) {
  if (minutes == null || Number.isNaN(Number(minutes))) return "—";
  const total = Math.max(0, Math.round(Number(minutes)));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}

export function formatRate(rate) {
  if (rate == null || Number.isNaN(Number(rate))) return "—";
  return `${Number(rate).toFixed(1).replace(/\.0$/, "")}%`;
}
